/**
 * test.js - Entry Point
 * Open121 Three.js Application
 */
import "./main.css";

// Modules
import { settings } from "./modules/settings.js";
import { setupLights } from "./modules/scene.js";
import { switchObject, currentMesh, changeDonutColor, setDonutVisible } from "./modules/mesh.js";
import { loadAllTextures } from "./modules/textures.js";
import { initInput, isMobileDevice, initAutoPlay } from "./modules/input.js";
import { tick } from "./modules/animation.js";
import {
  createHair,
  setHairColors,
  setHairLength,
  setBendStrength,
  setGravityStrength,
  setHairCount,
} from "./modules/hair.js";
import {
  loadLetterMeshes,
  setHairColors as setParticleMorphHairColors,
  setHairLength as setParticleMorphHairLength,
  setBendStrength as setParticleMorphBendStrength,
  setGravityStrength as setParticleMorphGravityStrength,
  setMeshColor as setParticleMorphMeshColor,
} from "./modules/particleMorph.js";
import {
  setDissolveEdgeColor,
  setDissolveEdgeWidth,
  setDissolveNoiseScale,
  setDissolvePulseColor,
  setDissolveGlowColor,
  setDissolvePulseSpeed,
  setDissolvePulseIntensity,
  setDissolveVeinDensity,
} from "./modules/dissolve.js";
import { loadWord, updateWordDisplay } from "./modules/wordDisplay.js";
import { urlSettings, setDynamicSettingsCallback, applyDynamicUrlSettings } from "./modules/urlParams.js";

/**
 * Blendet das Logo-Bild ein
 */
function fadeInLogo() {
  const logoElement = document.querySelector(".container .yes img");
  if (logoElement) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        logoElement.classList.add("visible");
      });
    });
  }
}

/**
 * Markiert die Szene als bereit und blendet Logo ein
 */
function setSceneReady() {
  fadeInLogo();

  // Benachrichtige Parent-Window dass Szene bereit ist (für Webflow iframe fade-in)
  if (window.parent !== window) {
    window.parent.postMessage({ type: "OPEN121_READY" }, "*");
  }
}

/**
 * Wendet dynamische URL-Settings an (nach Objekt-Erstellung)
 */
function applyDynamicSettings(urlSettings) {
  // --- Donut-Settings ---
  if (urlSettings.donutColor) {
    changeDonutColor(urlSettings.donutColor);
  }

  // --- Gemeinsame Haar-Settings (für beide Modi) ---
  if (urlSettings.hairColorRoot || urlSettings.hairColorTip) {
    setHairColors(urlSettings.hairColorRoot || null, urlSettings.hairColorTip || null);
  }
  if (urlSettings.hairLength !== undefined) {
    setHairLength(urlSettings.hairLength);
  }
  if (urlSettings.hairBendStrength !== undefined) {
    setBendStrength(urlSettings.hairBendStrength);
  }
  if (urlSettings.hairGravityStrength !== undefined) {
    setGravityStrength(urlSettings.hairGravityStrength);
  }
  if (urlSettings.hairCount !== undefined) {
    setHairCount(urlSettings.hairCount);
  }

  // --- Donut-spezifische Haar-Settings ---
  if (urlSettings.donutHairColorRoot || urlSettings.donutHairColorTip) {
    setHairColors(urlSettings.donutHairColorRoot || null, urlSettings.donutHairColorTip || null);
  }
  if (urlSettings.donutHairLength !== undefined) {
    setHairLength(urlSettings.donutHairLength);
  }
  if (urlSettings.donutHairBendStrength !== undefined) {
    setBendStrength(urlSettings.donutHairBendStrength);
  }
  if (urlSettings.donutHairGravityStrength !== undefined) {
    setGravityStrength(urlSettings.donutHairGravityStrength);
  }
  if (urlSettings.donutHairCount !== undefined) {
    setHairCount(urlSettings.donutHairCount);
  }

  // --- Dissolve-Settings ---
  if (urlSettings.dissolveEdgeColor) {
    setDissolveEdgeColor(urlSettings.dissolveEdgeColor);
  }
  if (urlSettings.dissolveEdgeWidth !== undefined) {
    setDissolveEdgeWidth(urlSettings.dissolveEdgeWidth);
  }
  if (urlSettings.dissolveNoiseScale !== undefined) {
    setDissolveNoiseScale(urlSettings.dissolveNoiseScale);
  }
  if (urlSettings.dissolvePulseColor) {
    setDissolvePulseColor(urlSettings.dissolvePulseColor);
  }
  if (urlSettings.dissolveGlowColor) {
    setDissolveGlowColor(urlSettings.dissolveGlowColor);
  }
  if (urlSettings.dissolvePulseSpeed !== undefined) {
    setDissolvePulseSpeed(urlSettings.dissolvePulseSpeed);
  }
  if (urlSettings.dissolvePulseIntensity !== undefined) {
    setDissolvePulseIntensity(urlSettings.dissolvePulseIntensity);
  }
  if (urlSettings.dissolveVeinDensity !== undefined) {
    setDissolveVeinDensity(urlSettings.dissolveVeinDensity);
  }

  // --- Logo/Bild Settings ---
  if (urlSettings.logoImage) {
    const logoElement = document.querySelector(".container .yes img");
    if (logoElement) logoElement.src = urlSettings.logoImage;
  }
  if (urlSettings.imageFilter) {
    const logoElement = document.querySelector(".container .yes img");
    if (logoElement) {
      const filters = {
        invert: "invert(100%)",
        grayscale: "grayscale(100%)",
        blur: "blur(5px)",
        brightness: "brightness(150%)",
      };
      logoElement.style.filter = filters[urlSettings.imageFilter] || "none";
    }
  }
  if (urlSettings.imageGrayscale !== undefined) {
    const logoElement = document.querySelector(".container .yes img");
    if (logoElement) {
      logoElement.style.filter = urlSettings.imageGrayscale ? "grayscale(100%)" : "none";
    }
  }
  if (urlSettings.blendMode) {
    const container = document.querySelector(".container .yes");
    if (container) {
      container.style.mixBlendMode = urlSettings.blendMode === "screen" ? "screen" : "difference";
    }
  }

  // --- Bild sichtbar/versteckt ---
  if (urlSettings.imageVisible !== undefined) {
    const logoElement = document.querySelector(".container .yes img");
    if (logoElement) {
      logoElement.style.display = urlSettings.imageVisible ? "block" : "none";
    }
  }

  // --- Bild-Größe ---
  if (urlSettings.imageSize !== undefined) {
    const logoElement = document.querySelector(".container .yes img");
    if (logoElement) {
      // Größe als Prozent (z.B. 50 = 50%, 100 = 100%, 150 = 150%)
      logoElement.style.width = `${urlSettings.imageSize}%`;
      logoElement.style.height = "auto";
    }
  }
}

/**
 * Initialisiert die Szene nach dem Laden
 */
function initScene() {
  // Callback für dynamische Settings registrieren
  setDynamicSettingsCallback(applyDynamicSettings);

  // Lichter einrichten
  setupLights();

  // Objekt erstellen
  const donut = switchObject("Donut");

  // Word-Display (ganzes Wort nebeneinander)
  if (settings.wordDisplay) {
    // Donut verstecken
    if (donut) {
      donut.visible = false;
    }

    // Wort laden und anzeigen
    loadWord(settings.wordDisplayText, (wordGroup) => {
      console.log("Word display ready");

      // Dynamische URL-Settings anwenden
      applyDynamicUrlSettings();

      setSceneReady();
    });

    // Animation starten
    tick();
    return;
  }

  // Partikel-Morphing für Buchstaben
  if (settings.particleMorph) {
    // Donut verstecken
    if (donut) {
      donut.visible = false;
    }

    // Buchstaben-Meshes laden und Partikel-System erstellen
    loadLetterMeshes((particleSystem) => {
      console.log("Particle morph system ready");

      // Dynamische URL-Settings anwenden
      applyDynamicUrlSettings();

      setSceneReady();

      // Auto-Play starten wenn in Settings aktiviert
      initAutoPlay();
    }, settings.particleMorphText);

    // Animation starten
    tick();
    return;
  }

  // Haare auf Donut erstellen
  if (settings.hair && donut) {
    createHair(donut);
  }

  // Donut-Sichtbarkeit anwenden (für donutVisible=false)
  if (donut && settings.donutVisible === false) {
    donut.visible = false;
  }

  // Dynamische URL-Settings anwenden
  applyDynamicUrlSettings();

  // Szene als bereit markieren (für PostMessage)
  setSceneReady();

  // Animation starten
  tick();
}

/**
 * App initialisieren
 */
function init() {
  // Input-Events initialisieren
  initInput();

  // Texturen laden, dann Szene initialisieren
  loadAllTextures(initScene);
}

// App starten
init();
